import React from 'react';
import { Users, UserPlus, Trophy, ArrowRight, AlertCircle, Clock, MessageCircle } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { useAuth } from '../../contexts/AuthContext';
import { useSchoolRegistration } from '../../hooks/useConvex';
import { Id } from '../../../convex/_generated/dataModel';
import { Link } from 'react-router-dom';

export const DashboardPage: React.FC = () => {
    const { school } = useAuth();
    const registration = useSchoolRegistration(school?.school_id as Id<'schools'> | undefined);
    const loading = registration === undefined;

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[60vh]">
                <Loader size="lg" />
            </div>
        );
    }

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'approved': return 'success';
            case 'rejected': return 'error';
            case 'submitted': return 'warning';
            default: return 'neutral';
        }
    };



    return (
        <div className="space-y-8">
            {/* Welcome Section */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-brand/10 to-brand-light/5 border border-white/10 p-8 md:p-12">
                <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-brand/15 rounded-full blur-3xl pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-64 h-64 bg-brand-light/10 rounded-full blur-3xl pointer-events-none"></div>

                <div className="relative z-10">
                    <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4 tracking-tight">
                        Welcome, <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF991C] to-[#FFB84D]">{school?.school_name}</span>
                    </h1>
                    <p className="text-xl text-gray-300 max-w-2xl">
                        Manage your team, register participants, and track your progress for ENIGMA 2026.
                    </p>
                    {school?.branch_name && (
                        <div className="mt-4 inline-flex items-center px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300">
                            <span className="font-bold text-brand mr-2">Branch:</span> {school.branch_name}
                        </div>
                    )}
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="p-4 md:p-6 border-white/5 bg-black/40 backdrop-blur-xl hover:bg-white/5 transition-all duration-300 group">
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-3 rounded-xl bg-brand/10 text-brand group-hover:scale-110 transition-transform duration-300">
                            <Users className="w-6 h-6" />
                        </div>
                        <Badge variant={getStatusColor(registration?.status)} size="sm">
                            {registration?.status ? registration.status.toUpperCase() : 'NOT STARTED'}
                        </Badge>
                    </div>
                    <h3 className="text-gray-400 text-sm font-bold uppercase tracking-wider mb-1">Participants</h3>
                    <p className="text-4xl font-heading font-bold text-white group-hover:text-brand transition-colors">
                        {registration?.total_participants || 0}
                    </p>
                </Card>

                <Card className="p-4 md:p-6 border-white/5 bg-black/40 backdrop-blur-xl hover:bg-white/5 transition-all duration-300 group">
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-3 rounded-xl bg-gold-500/10 text-gold-500 group-hover:scale-110 transition-transform duration-300">
                            <Trophy className="w-6 h-6" />
                        </div>
                    </div>
                    <h3 className="text-gray-400 text-sm font-bold uppercase tracking-wider mb-1">Events Entered</h3>
                    <p className="text-4xl font-heading font-bold text-white group-hover:text-gold-500 transition-colors">
                        {/* Calculate unique events from students array if available, otherwise 0 */}
                        {registration?.students ? new Set(registration.students.flatMap(s => s.events)).size : 0}
                    </p>
                </Card>

                <Card className="p-4 md:p-6 border-white/5 bg-black/40 backdrop-blur-xl hover:bg-white/5 transition-all duration-300 group">
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-3 rounded-xl bg-yellow-500/10 text-yellow-500 group-hover:scale-110 transition-transform duration-300">
                            <Clock className="w-6 h-6" />
                        </div>
                    </div>
                    <h3 className="text-gray-400 text-sm font-bold uppercase tracking-wider mb-1">Last Updated</h3>
                    <p className="text-lg font-mono text-white mt-2">
                        {(() => {
                            const timestamp = registration?.last_edited || registration?.submitted_at;
                            if (!timestamp) return 'Never';
                            const date = new Date(timestamp);
                            return isNaN(date.getTime()) ? 'Never' : date.toLocaleDateString();
                        })()}
                    </p>
                </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-6 md:p-8 border-white/5 bg-gradient-to-br from-brand/5 to-transparent hover:border-brand/30 hover:bg-brand/5 transition-all duration-300 group overflow-hidden">
                    <div>
                        <div className="w-12 h-12 rounded-2xl bg-brand text-black flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                            <UserPlus className="w-6 h-6" />
                        </div>
                        <h3 className="text-2xl font-heading font-bold text-white mb-2">Register Participants</h3>
                        <p className="text-gray-400 mb-6 h-12">
                            Add students to your team and assign them to events.
                        </p>
                        <Link to="/school/register">
                            <Button variant="primary" className="w-full transition-shadow">
                                {registration?.status === 'submitted' || registration?.status === 'approved'
                                    ? 'View Registration'
                                    : 'Start Registration'} <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                            </Button>
                        </Link>
                    </div>
                </Card>

                <Card className="p-6 md:p-8 border-white/5 bg-gradient-to-br from-brand/5 to-transparent hover:border-brand-light/30 hover:bg-brand-light/5 transition-all duration-300 group overflow-hidden">
                    <div>
                        <div className="w-12 h-12 rounded-2xl bg-brand-light text-white flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                            <Users className="w-6 h-6" />
                        </div>
                        <h3 className="text-2xl font-heading font-bold text-white mb-2">View My Team</h3>
                        <p className="text-gray-400 mb-6 h-12">
                            Review your team roster, download ID cards, and manage details.
                        </p>
                        <Link to="/school/team">
                            <Button variant="outline" className="w-full hover:bg-brand/10 hover:text-brand hover:border-brand/50">
                                Manage Team <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                            </Button>
                        </Link>
                    </div>
                </Card>
            </div>

            {/* Registration Status Banner */}
            {registration?.status === 'submitted' && (
                <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-start gap-4">
                    <Clock className="w-6 h-6 text-yellow-500 mt-1" />
                    <div>
                        <h4 className="text-lg font-bold text-yellow-500 mb-1">Registration Submitted</h4>
                        <p className="text-gray-300 text-sm">
                            Your registration has been submitted and is pending approval by the admin team. You will be notified once it is approved.
                        </p>
                        <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-3">
                            <MessageCircle className="w-5 h-5 text-green-500" />
                            <div>
                                <p className="text-sm text-gray-300">
                                    Need to make changes? Contact us on WhatsApp:
                                </p>
                                <a
                                    href="https://wa.me/919452900015"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-400 font-bold hover:text-green-300 transition-colors"
                                >
                                    +91 9452900015
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {registration?.status === 'rejected' && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-4">
                    <AlertCircle className="w-6 h-6 text-red-500 mt-1" />
                    <div>
                        <h4 className="text-lg font-bold text-red-500 mb-1">Action Required</h4>
                        <p className="text-gray-300 text-sm">
                            Your registration was returned. Please review the feedback and resubmit.
                        </p>
                        {registration.rejection_reason && (
                            <div className="mt-2 p-3 rounded bg-black/30 text-red-400 text-sm font-mono border border-red-500/20">
                                Reason: {registration.rejection_reason}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
